import AssemblerLogo from 'assets/images/AssemblerLogo';
import * as React from 'react';
import { useSelector, useDispatch, batch } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import Button from 'components/Button';
import Icon from 'components/Icon';
import { IconType } from 'components/Icon/constants';
import UndoRedoControl from 'components/UndoRedo/UndoRedoControl';
import ZoomControl from 'components/ZoomControl';
import { ASSEMBLER_HELP, AppRoute, DEFAULT_ZOOM_SIZE, ExportType, ModalType, ProcessType } from 'const';
import { HeaderIds } from 'const/Header';
import { aiTranslate as aiTranslateAction } from 'containers/AITranslation/actions';
import {
  importTranslationPackage as importTranslationPackageAction,
  saveProjectLocally as saveProjectLocallyAction,
  togglePreview,
  startHandlingReusableLayoutsEditing, stopHandlingReusableLayoutsEditing } from 'containers/App/actions';
import * as AppModels from 'containers/App/models';
import { resetPreviewsHtml } from 'containers/ArtboardPreview/actions';
import { showModal as showModalAction } from 'containers/ModalManager/actions';
import { exportPackage as exportPackageAction } from 'containers/ModalWindows/Export/actions';
import { setActiveLayer as setActiveLayerAction } from 'containers/Project/actions';
import * as ProjectModels from 'containers/Project/models';
import { redo, undo } from 'containers/UndoRedoControl/actions';
import { RootDocumentMap } from 'models';
import { convertTime } from 'utils/convertTime';
import { intlGet } from 'utils/intlGet';
import Translation from './Controls/Translation';
import { headerSelector, headerUndoRedoSelector } from './selectors';
import styles from './styles.module.scss';

const HEADER_BUTTON = 'Header.Button';
const HEADER_LABEL = 'Header.Label';

export type HeaderProps = { rootDocument: RootDocumentMap } & RouteComponentProps;

export const Header: React.FunctionComponent<HeaderProps> = (props) => {
  const { rootDocument, match, history } = props;
  const dispatch = useDispatch();
  const {
    activeLayer,
    activeSurfaceId,
    activeProcessType,
    areImagesBeingDownloaded,
    areReusableLayoutsBeingUploaded,
    countryAbbreviations,
    exportForTranslationsEnabled,
    isOpenToolbar,
    isTranslationAvailable,
    isUploadAvailable,
    lastLocalSaveTime,
    originalCountry,
    originalLanguage,
  } = useSelector(headerSelector);
  const setActiveLayerHandler: ProjectModels.ActionCreator.SetActiveLayer = (layer) => {
    return dispatch(setActiveLayerAction(layer));
  };
  const exportPackageHandler = (debugFlag = false): void => {
    dispatch(exportPackageAction(ExportType.TRANSLATION_PACKAGE, debugFlag));
  };
  const importTranslationPackageHandler = (packageFile: File): void => {
    dispatch(importTranslationPackageAction(packageFile));
  };
  const aiTranslateHandler = (): void => {
    dispatch(aiTranslateAction());
  };
  const onUploadClick = (): void => {
    dispatch(showModalAction(ModalType.UPLOAD_PROJECT));
  };
  const onExportClick = (): void => {
    dispatch(showModalAction(ModalType.EXPORT));
  };
  const onSaveClick = (): void => {
    dispatch(saveProjectLocallyAction());
  };
  const showProjectDetails = (): void => {
    dispatch(showModalAction(ModalType.PROJECT_DETAILS));
  };
  const { isRedoDisabled, isUndoDisabled } = useSelector(headerUndoRedoSelector);

  const goToPreview = (): void => {
    batch(() => {
      dispatch(resetPreviewsHtml());
      dispatch(togglePreview(true, DEFAULT_ZOOM_SIZE));
    });

    history.push(`${match.url}/${AppRoute.PREVIEW}/${activeSurfaceId}`);
  };
  const onRedo = (): void => { dispatch(redo()); };
  const onUndo = (): void => { dispatch(undo()); };
  const onStartHandlingReusableLayoutsEditing: AppModels.ActionCreator.StartHandlingReusableLayoutsEditing = () => {
    return dispatch(startHandlingReusableLayoutsEditing());
  };
  const onStopHandlingReusableLayoutsEditing: AppModels.ActionCreator.StopHandlingReusableLayoutsEditing = () => {
    return dispatch(stopHandlingReusableLayoutsEditing());
  } ;

  const getExternalActionsSection = (): JSX.Element => {

    return (
      <div className={styles.externalActionsContainer}>
        {
          lastLocalSaveTime && activeProcessType !== ProcessType.AI_TRANSLATION && activeProcessType !== ProcessType.IMPORT_TRANSLATION_PACKAGE ?
            <>
              <div className={styles.saveTime}>{intlGet('Header', 'SavedAt', { time: convertTime(lastLocalSaveTime) })}</div>
              <div className={styles.spacer} />
            </>
            : null
        }
        <div className={styles.externalAction}>
          <Button
            icon={IconType.SAVE_PROJECT}
            title={intlGet(HEADER_BUTTON, 'Save')}
            onClick={onSaveClick}
            highlightOnHover={true}
          />
        </div>
        {/**
         * The id in the following code is here because we need to manually block
         * the 'Upload to PromoMats' button from clicking when toolbar is opened (DCC-4278)
        */}
        <div className={styles.externalAction} id="uploadButton">
          <Button
            icon={IconType.UPLOAD_PROJECT}
            size="sm-md"
            title={intlGet(HEADER_BUTTON, 'Upload')}
            disabled={!isUploadAvailable || areImagesBeingDownloaded || isOpenToolbar}
            onClick={onUploadClick}
            highlightOnHover={true}
          />
        </div>
        <div className={styles.externalAction}>
          <Button
            icon={IconType.EXPORT_PROJECT}
            title={intlGet(HEADER_BUTTON, 'Export')}
            disabled={areImagesBeingDownloaded}
            onClick={onExportClick}
            highlightOnHover={true}
          />
        </div>
        {/**
         * The id in the following code is here because we need to manually block
         * the 'Preview' button from clicking when toolbar is opened (DCC-2711)
        */}
        <div className={styles.previewTitle} id="previewButton">
          <Button
            onClick={goToPreview}
            disabled={areImagesBeingDownloaded || isOpenToolbar}
            title={intlGet(HEADER_LABEL, 'Preview')}
            icon={IconType.VIEW_DETAILS}
            size="md"
            highlightOnHover={true}
          />
        </div>
      </div>
    );
  };

  const language = rootDocument.get('language').first();
  const countryLabel = rootDocument.get('country').map(country => countryAbbreviations.get(country ?? '')).join(', ');
  const originalCountryLabel = originalCountry.map(country => countryAbbreviations.get(country ?? '')).join(', ');
  const name = rootDocument.get('name');

  return (
    <header className={styles.Header}>
      <div className={styles.section}>
        <div className={styles.sectionLeft}>
          <AssemblerLogo />
          <div className={styles.projectTitleContainer}>
            <span className={styles.projectTitle}>{name}</span>
          </div>
          <div className={styles.projectDetailsContainer} title={intlGet(HEADER_BUTTON, 'ViewProjectDetails')}>
            <Button icon={IconType.EXCLAMATION_MARK} size="sm" onClick={showProjectDetails} />
          </div>
          <div className={styles.spacer} />
          <div className={styles.helpContainer}>
            <a href={ASSEMBLER_HELP} target="_blank">
              <Icon color="primary" size="sm" type={IconType.QUESTION_MARK}/>
              <span className={styles.helpTitle}>{intlGet(HEADER_LABEL, 'Help')}</span>
            </a>
          </div>
          <div className={styles.spacer} />
          {
            isTranslationAvailable &&
            <>
              <Translation
                activeLayer={activeLayer}
                originalLang={`${originalLanguage.first()} - ${originalCountryLabel}`}
                language={`${language} - ${countryLabel}`}
                toggleLayer={setActiveLayerHandler}
                disabled={areReusableLayoutsBeingUploaded}
                onImport={exportForTranslationsEnabled ? importTranslationPackageHandler : null}
                onExport={exportForTranslationsEnabled ? exportPackageHandler : null }
                onAITranslate={exportForTranslationsEnabled ? aiTranslateHandler : null}
              />
              <div className={styles.spacer} />
            </>
          }
          <ZoomControl />
          <div className={styles.spacer} />
          <div id={HeaderIds.UNDO_REDO} className={styles.container}>
            <UndoRedoControl
              redo={onRedo}
              undo={onUndo}
              startHandlingReusableLayoutsEditing={onStartHandlingReusableLayoutsEditing}
              stopHandlingReusableLayoutsEditing={onStopHandlingReusableLayoutsEditing}
              isRedoDisabled={isRedoDisabled}
              isUndoDisabled={isUndoDisabled}
            />
          </div>
          <div className={styles.spacer} />
        </div>
        <div className={styles.sectionRight}>
          {getExternalActionsSection()}
        </div>
      </div>
    </header>
  );
};

const HeaderWithRouter = withRouter(Header);

export default HeaderWithRouter;
